import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getMovementTypes, getProductMovementsList, selectMovementTypes, selectMovementTypesLoading, selectProductMovementsData, selectProductMovementsLoading } from "../redux/productMovements/productMovementsSlice";
import DynamicIcon from "../components/DynamicIcon";
import ProductMovementLine from "../components/productMovementsPage/ProductMovementLine";
import ListPagination from "../components/templates/ListPagination";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import DynamicTitle from "../components/DynamicTitle";
import moment from "moment";
import AddEditProductMovement from "../components/productMovementsPage/AddEditProductMovement";

export default function ProductMovementsList() {
    const dispatch = useDispatch();
    const movementsList = useSelector(selectProductMovementsData);
    const movementsLoading = useSelector(selectProductMovementsLoading);
    const movementTypes = useSelector(selectMovementTypes);
    const movementTypesLoading = useSelector(selectMovementTypesLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [movementTypeFilter, setMovementTypeFilter] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalMovementId = useSelector(selectRightModalId);
    
    // Carico i tipi di movimento quando il componente viene montato
    useEffect(() => {
        if (accountData?._id && !movementTypes?.length) {
            dispatch(getMovementTypes({
                accountId: accountData._id,
                token
            }));
        }
    }, [dispatch, accountData, movementTypes?.length]);
    
    // Funzione che verrà passata al componente ListPagination
    const handlePageUpdate = (start, length) => {
        dispatch(getProductMovementsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            movementType: movementTypeFilter,
            startDate: startDateFilter ? moment(startDateFilter).format('YYYY-MM-DD') : '',
            endDate: endDateFilter ? moment(endDateFilter).format('YYYY-MM-DD') : ''
        }));
        dispatch(resetRefreshNow());
    };

    // Eseguo la chiamata iniziale quando il componente viene montato o quando cambiano i filtri
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getProductMovementsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0, // Inizio da zero
                length: 10, // Uso la dimensione della pagina predefinita
                movementType: movementTypeFilter,
                startDate: startDateFilter ? moment(startDateFilter).format('YYYY-MM-DD') : '',
                endDate: endDateFilter ? moment(endDateFilter).format('YYYY-MM-DD') : ''
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, searchTerm, movementTypeFilter, startDateFilter, endDateFilter]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);

    // Gestisce il reset dei filtri
    const handleResetFilters = () => {
        setSearchTerm('');
        setMovementTypeFilter('');
        setStartDateFilter('');
        setEndDateFilter('');
    };
    
    return (
        <div className="page">
            {movementsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Movimenti Prodotti" />
            
            <h1>Movimenti Prodotti</h1>
            <div className="box">
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-96">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <DynamicIcon iconClass="fa-solid fa-search" />
                        </div>
                        <input 
                            type="text" 
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5" 
                            placeholder="Cerca movimenti..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button 
                                className="absolute inset-y-0 end-0 flex items-center pe-3"
                                onClick={() => setSearchTerm('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" />
                            </button>
                        )}
                    </div>
                    
                    {/* Filtri */}
                    <div className="flex flex-wrap gap-2">
                        {/* Filtro per Tipo Movimento */}
                        <select 
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={movementTypeFilter}
                            onChange={(e) => setMovementTypeFilter(e.target.value)}
                            disabled={movementTypesLoading}
                        >
                            <option key="all-types" value="">Tutti i tipi</option>
                            {movementTypes && movementTypes.map((type, index) => (
                                <option key={`movement-type-${index}`} value={type.value || type}>
                                    {type.label || type}
                                </option>
                            ))}
                        </select>

                        {/* Filtro Data Inizio */}
                        <div className="flex items-center">
                            <label className="text-sm text-gray-700 mr-2">Da:</label>
                            <input 
                                type="date" 
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                                value={startDateFilter}
                                onChange={(e) => setStartDateFilter(e.target.value)}
                            />
                        </div>

                        {/* Filtro Data Fine */}
                        <div className="flex items-center">
                            <label className="text-sm text-gray-700 mr-2">A:</label>
                            <input 
                                type="date" 
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                                value={endDateFilter}
                                onChange={(e) => setEndDateFilter(e.target.value)}
                            />
                        </div>

                        {/* Bottone reset filtri */}
                        <button 
                            className="secondary p-2.5" 
                            onClick={handleResetFilters}
                        >
                            <DynamicIcon iconClass="fa-solid fa-filter-circle-xmark" />
                            Reset
                        </button>
                        
                        {/* Bottone Nuovo Movimento */}
                        <button 
                            className="primary" 
                            onClick={() => dispatch(setModal({ mode: 'add' }))}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Nuovo Movimento
                        </button>
                    </div>
                </div>

                <ListPagination
                    initTotalElements={movementsList?.recordsFiltered || 0}
                    onPageChange={handlePageUpdate}
                >
                    {/* Tabella movimenti */}
                    <div className="w-full text-sm flex flex-col gap-2">
                        {/* Headers */}
                        <div className="w-full flex justify-between font-bold">
                            <div className="px-6 py-4 w-1/6">DATA</div>
                            <div className="px-6 py-4 w-1/5">PRODOTTO</div>
                            <div className="px-6 py-4 w-1/6">TIPO</div>
                            <div className="px-6 py-4 w-1/6 text-right">QUANTITÀ</div>
                            <div className="px-6 py-4 w-1/6 text-right">PREZZO</div>
                            <div className="px-6 py-4 w-1/6 flex justify-end">AZIONI</div>
                        </div>
                        
                        {/* Rows */}
                        {
                            movementsList?.data?.map((movement, index) => (
                                <ProductMovementLine
                                    key={movement._id}
                                    movementData={movement}
                                    index={index}
                                />
                            ))
                        }
                        
                        {/* Messaggio quando non ci sono dati */}
                        {(!movementsList?.data || movementsList.data.length === 0) && !movementsLoading && (
                            <div className="w-full py-4 text-center text-gray-500" key="no-data-message">
                                Nessun movimento trovato
                            </div>
                        )}
                    </div>
                </ListPagination>
            </div>

            {/* RIGHT MODAL per inserimento o visualizzazione movimento */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {rightModalMode === 'add' && (
                            <AddEditProductMovement />
                        )}
                        {
                            rightModalMode === 'view' && rightModalMovementId && (
                                <div>
                                    {/* Qui verrà implementato il componente ViewProductMovement */}
                                    <h2>Dettagli Movimento</h2>
                                    <p>ID: {rightModalMovementId}</p>
                                </div>
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}
